import {
    createAppointment,
    IAppointment,
} from '../../../../API/GoMoto';
import { useAdminState } from '../../../../context/Admin/admin';
import { useEmptyAppointmentFactory } from '../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../context/Localization/localization';
import { SearchGrid } from '../../../../controls/SearchGrid/SearchGrid/SearchGrid';
import { Filters } from './Filters/Filters';
import { AppointmentLoanerOrRentalOption, AppointmentType } from '../../../../API/Enums';
import { CheckInsProps } from './CheckIns.types';
import { LocalizationType } from '../../../../context/Localization/localization.types';
import { GoMotoComponent, GoMotoFunction } from '../../../../API/GoMoto.types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SearchGridProps, SearchGridRow } from '../../../../controls/SearchGrid/SearchGrid/SearchGrid.types';
import { GenericClickableOnClickFunction } from '../../../../controls/GenericClickable/GenericClickable.types';
import { AppointmentTileType } from '../../../../controls/SearchGrid/SearchGrid/SearchGridAppointmentTile/SearchGridAppointmentTile.types';
import { useAdminFiltersDispatch, useAdminFiltersState, useAdminFiltersFetchAppointments } from '../../../../context/AdminFilters/AdminFilters';
import { AdminFiltersDispatchTypes } from '../../../../context/AdminFilters/AdminFilters.types';
import { AdminContextState } from '../../../../context/Admin/admin.types';
import { AdminLockerOperationDispatch, AdminLockerOperationDispatchTypes } from '../../../../context/AdminLockerOperation/adminLockerOperation.types';
import { useAdminLockerOperationDispatch, useAdminLockerOperationState } from '../../../../context/AdminLockerOperation/adminLockerOperation';

/**
 * Displays the Loaded Keys and Check-Ins tab and handles all logic for pagination, filters, API calls, and displaying
 * the appointment tiles to the screen.
 *
 * @param tabType - {@link CheckInsProps.tabType tabType} description.
 * @param onAppointmentSelected - {@link CheckInsProps.onAppointmentSelected onAppointmentSelected} description.
 * @param onAddKeyForAppointment - {@link CheckInsProps.onAddKeyForAppointment onAddKeyForAppointment} description.
 */
export const CheckIns: GoMotoComponent<CheckInsProps> = ({
    tabType,
    onAppointmentSelected,
    onAddKeyForAppointment,
    keyUpdated,
    setKeyUpdated,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { key: kioskKey } = useKioskState();
    const { LastLockerChanged } = useAdminState();
    const { hasPriorityAppointments, filterValues, appointments, results, status } = useAdminFiltersState();
    const { fetchAppointments } = useAdminFiltersFetchAppointments();
    const adminFilterDispatch = useAdminFiltersDispatch();
    const { isAdminCreate, keyStored } = useAdminLockerOperationState();
    const adminLockerOperationDispatch: AdminLockerOperationDispatch = useAdminLockerOperationDispatch();
    const factory = useEmptyAppointmentFactory(true);

    const lastLockerRef = useRef<AdminContextState['LastLockerChanged']>(LastLockerChanged);

    const includeFilters: AppointmentType[] = useMemo(() => {
        if (tabType === 'check-ins') {
            const filters: AppointmentType[] = [AppointmentType.none, AppointmentType.checkedIn, AppointmentType.created, AppointmentType.dmsAppointment];

            if (hasPriorityAppointments) {
                filters.push(AppointmentType.priority);
            }

            return filters;
        }
        else {
            const filters: AppointmentType[] = [AppointmentType.none, AppointmentType.keyDroppedOff, AppointmentType.inKeyBin];

            return filters;
        }
    }, [hasPriorityAppointments, tabType]);

    useEffect(() => {
        if (keyUpdated) {
            adminFilterDispatch({ type: AdminFiltersDispatchTypes.Reinitialize });
            setKeyUpdated(false);
        }
    });

    /**
     * After a store-key locker operation completes, the backend exhibits read-after-write lag,
     * so the initial mount fetch can miss the freshly stored tile. Schedule a delayed re-fetch
     * to land after propagation, then clear the flag.
     */
    useEffect(() => {
        if (keyStored && tabType === 'loaded-keys') {
            const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
                adminFilterDispatch({ type: AdminFiltersDispatchTypes.Reinitialize });
                adminLockerOperationDispatch({
                    type: AdminLockerOperationDispatchTypes.SetKeyStored,
                    payload: false,
                });
            }, 1000);

            return () => clearTimeout(timer);
        }

        return undefined;
    }, [keyStored, tabType, adminFilterDispatch, adminLockerOperationDispatch]);

    /**
     * When a locker has changed, re-initialize the filters context so we grab a new list
     * of appointments.
     */
    useEffect(() => {
        if (lastLockerRef.current !== LastLockerChanged) {
            lastLockerRef.current = LastLockerChanged;
            adminFilterDispatch({ type: AdminFiltersDispatchTypes.Reinitialize });
        }
    }, [LastLockerChanged, adminFilterDispatch]);

    /**
     * Fetches the appointments when the component mounts or filters change.
     *
     * Skips the initialize-fetch when a store-key just completed on the Loaded Keys tab —
     * the delayed re-fetch effect above will handle it after backend propagation, avoiding
     * a duplicate `GetActiveAppointments` call that would race the just-stored tile.
     */
    useEffect(() => {
        if (status === 'initialize' && keyStored && tabType === 'loaded-keys') {
            return;
        }

        if (status === 'initialize' || status === 'search') {
            fetchAppointments(tabType);
        }
    }, [fetchAppointments, status, tabType, keyStored]);

    const convertToInternal = useCallback(async ({ appt, viewOnly = false }: { appt: IAppointment | null; viewOnly?: boolean; }) => {
        if (appt) {
            if (!appt.key && appt.dmsAppointmentKey) {
                if (appt.dmsAppointmentKey) {
                    appt = {
                        ...factory(),
                        ...appt,
                    };
                    if (!viewOnly) {
                        const newApptKey = await createAppointment(kioskKey, appt);
                        appt = {
                            ...appt,
                            key: newApptKey,
                            convertedFromDms: true,
                        };
                    }
                }
            }
        }

        return appt !== null && filterValues?.type === AppointmentType.dmsAppointment ? {
            ...appt,
            convertedFromDms: true,
        } : appt;
    }, [factory, kioskKey, filterValues?.type]);

    /**
     * This function formats the 1-dimensional results array into a 2-dimensional array where each "row" has a fixed length of 3 "columns".
     * The arrays contain an object of props for each tile that will be rendered to the screen and is used to paginate the tiles.
     *
     * @returns undefined when results is null, an empty array when there are no results, or a formatted array when the results array has a length greater than 0.
     */
    const getAppointmentTiles: GoMotoFunction<void, SearchGridProps['gridTiles']> = () => {
        if (results !== null && results.length > 0) {
            const formattedAppointmentArray: IAppointment[][] = [];

            for (let index = 0; index < results.length; index += 3) {
                formattedAppointmentArray.push(results.slice(index, index + 3));
            }

            return formattedAppointmentArray.map((appointmentRow: IAppointment[]) => {
                return appointmentRow.map((appointment: IAppointment) => {
                    const isExchangeKeyStored: boolean = appointment.returningLoanerOrRental !== null
                        && appointment.returningLoanerOrRental !== AppointmentLoanerOrRentalOption.None
                        && (appointment.keyStatus === 'loaner key stored' || appointment.keyStatus === 'rental key stored');
                    const description: string | undefined = isExchangeKeyStored ?
                        appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ?
                            Localization.LoanerReturn :
                            Localization.RentalReturn :
                        undefined;

                    const handleOnAddKeyForAppointment: GoMotoFunction<void, Promise<void>> = async () => {
                        if (isAdminCreate) {
                            adminLockerOperationDispatch({
                                type: AdminLockerOperationDispatchTypes.SetIsAdminCreate,
                                payload: false,
                            });
                        }

                        if (onAddKeyForAppointment) {
                            const internalAppt: IAppointment | null = await convertToInternal({
                                appt: {
                                    ...appointment,
                                    status: 'scheduled',
                                },
                            });

                            onAddKeyForAppointment(internalAppt);
                        }
                    };

                    const handleOnClick: GenericClickableOnClickFunction = async () => {
                        const internalAppt: IAppointment | null = await convertToInternal({
                            appt: {
                                ...appointment,
                                status: !appointment.key ? 'not scheduled' : appointment.status,
                            },
                            viewOnly: true,
                        });

                        onAppointmentSelected(internalAppt);
                    };

                    const getTileType: GoMotoFunction<void, AppointmentTileType> = () => {
                        if (appointments.active.includes(appointment)) {
                            return 'active';
                        }
                        else if (appointments.dms.includes(appointment)) {
                            return 'dms';
                        }
                        else if (appointments.priority.includes(appointment)) {
                            return 'priority';
                        }
                        else {
                            return 'none';
                        }
                    };

                    return {
                        appointment: appointment,
                        onAddKeyClick: onAddKeyForAppointment ? handleOnAddKeyForAppointment : undefined,
                        onClick: handleOnClick,
                        description: description,
                        appointmentTileType: getTileType(),
                        tabType: tabType,
                    };
                }) as SearchGridRow;
            });
        }
        else if (results !== null && results.length === 0) {
            return [];
        }
        else {
            return undefined;
        }
    };

    return (
        <SearchGrid
            filters={
                <Filters
                    includeFilters={includeFilters}
                    tabType={tabType}
                />
            }
            gridTiles={getAppointmentTiles()}
            noResultsLabel={Localization.formatString(Localization.NoAvailable, Localization.Appointments)
                .toString()}
        />
    );
};